/**
 * @file Shared utility functions for the calendar library.
 * These utilities are safe to use in both client and server environments.
 */

/**
 * Formats a date in Australian format (dd/mmm/yyyy).
 * Uses UTC date to avoid timezone issues in tests.
 */
export function formatDateAustralian(date: Date): string {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  
  return `${day}/${month}/${year}`;
}

/**
 * Calculates the duration between two dates in minutes.
 */
export function getDurationMinutes(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
}

/**
 * Checks if two dates are on the same day.
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return date1.toDateString() === date2.toDateString();
}

/**
 * Gets the start of week (Monday) for a given date.
 */
export function getStartOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  result.setDate(diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Gets the end of week (Sunday) for a given date.
 */
export function getEndOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? 0 : 7); // Adjust when day is Sunday
  result.setDate(diff);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Validates an email address.
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates a phone number (basic validation).
 */
export function isValidPhone(phone: string): boolean {
  // Basic validation for Australian phone numbers
  const phoneRegex = /^(\+61|0)[2-9]\d{8}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
}

/**
 * Generates a unique ID (simple implementation for client-side).
 * Note: For production, use a proper UUID library on the server.
 */
export function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
} 